import React, { useContext } from 'react'
import { Container, Row, Card, Col, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { ThemeContext } from '../components/ThemeContext.js'

const highlightItems = [
  {
    title: 'Lower waste',
    text: 'Reduce paper receipt volume and keep consumer transactions digital.'
  },
  {
    title: 'Better security',
    text: 'Store receipts in a protected, searchable digital format.'
  },
  {
    title: 'Cleaner operations',
    text: 'Streamline receipt validation and retrieval for merchants.'
  }
]

const HomeScreen = () => {
  const { theme } = useContext(ThemeContext)
  const navigate = useNavigate()

  return (
    <Container
      fluid='md'
      className='home-screen'
      style={{ backgroundColor: theme.bg, color: theme.text }}
    >
      <div
        className='home-hero'
        style={{
          background: `linear-gradient(135deg, ${theme.card} 0%, ${theme.surfaceMuted} 100%)`,
          borderColor: theme.sidebarAccent,
          boxShadow: `0 18px 44px ${theme.sidebarAccent}20`
        }}
      >
        <Row className='align-items-center g-4'>
          <Col lg={8}>
            <div className='home-eyebrow' style={{ color: theme.accent }}>
              Digital receipts for everyday commerce
            </div>
            <div className='home-mission-badge' style={{ color: theme.text }}>
              Our mission
            </div>
            <h1 className='home-title' style={{ color: theme.text }}>
              Paperless receipts for a cleaner, smarter retail experience.
            </h1>
            <p className='home-subtitle' style={{ color: theme.subtext }}>
              Global Receipt helps merchants replace paper with secure digital
              receipts, easier verification, and a more sustainable checkout.
            </p>
          </Col>
          <Col lg={4} className='d-flex justify-content-lg-end'>
            <div className='home-cta-stack'>
              <Button
                size='lg'
                onClick={() => navigate('/about')}
                style={{
                  borderColor: theme.accent,
                  backgroundColor: theme.accent,
                  color: theme.bg,
                  borderRadius: '999px',
                  fontWeight: 700,
                  padding: '0.8rem 1.35rem',
                  boxShadow: `0 10px 24px ${theme.accent}55`
                }}
              >
                Learn More
              </Button>
              <Button
                size='lg'
                variant='outline-primary'
                onClick={() => navigate('/businessregister')}
                style={{
                  borderColor: theme.accent,
                  color: theme.accent,
                  backgroundColor: 'transparent',
                  borderRadius: '999px',
                  fontWeight: 700,
                  padding: '0.8rem 1.35rem'
                }}
              >
                Register Your Business
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <Row className='g-3 home-stats'>
        {highlightItems.map((item) => (
          <Col md={4} key={item.title}>
            <div
              className='home-stat-card'
              style={{
                backgroundColor: theme.card,
                borderColor: theme.sidebarAccent,
                color: theme.text,
                boxShadow: `0 10px 26px ${theme.sidebarAccent}12`
              }}
            >
              <div
                className='home-stat-title'
                style={{ color: theme.accent, borderColor: theme.accent }}
              >
                {item.title}
              </div>
              <p style={{ margin: 0, color: theme.subtext }}>{item.text}</p>
            </div>
          </Col>
        ))}
      </Row>

      <Row className='g-4 mt-1'>
        <Col lg={7}>
          <Card
            className='home-panel'
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '16px'
            }}
          >
            <Card.Body className='p-4'>
              <Card.Title className='home-panel-title'>News</Card.Title>
              <Card.Text className='home-panel-copy'>
                <div style={{ marginBottom: '1rem', color: theme.subtext }}>
                  September 2026
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ color: theme.text }}>
                    Platform status.
                  </strong>
                </div>

                <p>
                  Global Receipt is progressing through two receipt-ingestion
                  tracks.
                </p>

                <p>
                  <strong style={{ color: theme.text }}>
                    Image-based receipts.
                  </strong>{' '}
                  This is currently the more mature path. It supports receipt
                  uploads, validation, OCR processing, normalization, digital
                  rendering, and storage, and is undergoing controlled testing
                  and production hardening have been completed. Lastly,
                  distributed ledger (Hashgraph HashSphere) testing is well
                  underway.
                </p>

                <p>
                  <strong style={{ color: theme.text }}>
                    POS data ingestion.
                  </strong>{' '}
                  In parallel, Global Receipt is developing a JSON-based
                  pipeline for transactions received from supported
                  point-of-sale providers. The platform normalizes
                  provider-specific data into a common receipt format and
                  generates a digital receipt image for the customer.
                </p>

                <p>
                  The resulting image is designed to provide a clear, consistent
                  receipt experience while preserving the transaction details
                  needed for storage, lookup, validation, and future receipt
                  services. A merchant POS transaction can become a branded
                  digital receipt without paper, manual scanning, or
                  customer-side OCR.
                </p>

                <p>
                  <strong style={{ color: theme.text }}>Integrations.</strong>{' '}
                  Clover is active in the sandbox environment, with API
                  connectivity and the webhook-to-order retrieval flow
                  confirmed. Square integration is also under development, with
                  provider-specific receipt mapping and validation in place.
                  Additional webhook processing and operational testing remain
                  before broader deployment.
                </p>

                <p>
                  Call for investment and partnerships, express interest at{' '}
                  <a
                    href='https://www.hermanosgazpachos.ca'
                    target='_blank'
                    rel='noreferrer'
                    style={{ color: theme.accent }}
                  >
                    hermanosgazpachos.ca
                  </a>
                </p>
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent,
                borderTop: `1px solid ${theme.sidebarAccent}`
              }}
            >
              <Button
                size='sm'
                variant='outline-primary'
                href='mailto:info@globalreceipt.ca'
                style={{
                  borderColor: theme.accent,
                  color: theme.accent,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.accent}`,
                  borderRadius: '999px',
                  fontWeight: 700,
                  padding: '0.55rem 1rem'
                }}
              >
                Contact Us
              </Button>
            </Card.Footer>
          </Card>
        </Col>

        <Col lg={5}>
          <Card
            className='home-panel'
            style={{
              backgroundColor: theme.card,
              color: theme.text,
              borderColor: theme.sidebarAccent,
              borderRadius: '16px'
            }}
          >
            <Card.Body className='p-4'>
              <Card.Title className='home-panel-title'>
                Why go paperless?
              </Card.Title>
              <Card.Text className='home-panel-copy'>
                <p>
                  There are millions of tonnes of paper per year that end up in
                  the waste/recycling stream, and our assumption at a minimum is
                  that three million tonnes are sales receipts.
                </p>

                <p>
                  90% of receipts are coated in BPA or BPS, chemicals that are
                  harmful to both humans and the environment. These chemicals
                  can leach into the soil and waterways when disposed of,
                  causing long-term environmental damage.
                </p>

                <p>
                  By switching to electronic receipts, we can significantly
                  reduce the demand for paper production, which in turn helps
                  conserve natural resources, reduce energy consumption, and
                  decrease greenhouse gas emissions associated with paper
                  manufacturing.
                </p>

                <p>
                  Our technology provides a private distributed ledger secured
                  electronic receipt to you at checkout by scanning your unique
                  QR code. It is easy to use, contactless, and environmental.
                </p>
              </Card.Text>
            </Card.Body>
            <Card.Footer
              style={{
                backgroundColor: theme.surfaceMuted,
                color: theme.subtext,
                borderColor: theme.sidebarAccent,
                borderTop: `1px solid ${theme.sidebarAccent}`
              }}
            >
              <Button
                size='sm'
                variant='outline-primary'
                onClick={() => navigate('/businessregister')}
                style={{
                  borderColor: theme.accent,
                  color: theme.accent,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.accent}`,
                  borderRadius: '999px',
                  fontWeight: 700,
                  padding: '0.55rem 1rem'
                }}
              >
                Register Your Business
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default HomeScreen
